import { Link } from "@inertiajs/react";
import Callout from "@/components/help/Callout";
import { contactMailto } from "@/lib/contact";
import Screenshot from "@/components/help/Screenshot";
import MilestonesCard from "@/components/schools/MilestonesCard";
import { CLASSROOM_MILESTONES } from "@/help/fixtures";

export default function SettingClassroomGoalsAndMilestones() {
  return (
    <>
      <p>
        Each classroom can have its own goals and rewards — a target the room is aiming for and something they earn together when they hit it. School admins and the teachers assigned to a classroom can both set these.
      </p>

      <h2>How they appear</h2>
      <p>
        Both show up on the classroom dashboard, with progress bars and a locked/unlocked icon — same way school rewards look, just for your room.
      </p>
      <Screenshot caption="The classroom rewards card">
        <MilestonesCard
          milestones={CLASSROOM_MILESTONES}
          title="Classroom-wide rewards"
          emptyDescription="Add classroom rewards to give your room something to chase together."
        />
      </Screenshot>

      <h2>Goals vs. rewards</h2>
      <p>
        A <strong>goal</strong> is just a number with a progress bar — a target your class is aiming for. Nothing's earned when they hit it; it's just a nudge.
      </p>
      <p>
        A <strong>reward</strong> is a goal with something attached: pajama day, read-outside Friday, a silly voice during read-aloud. When the classroom crosses the number, the reward unlocks for everyone in that classroom.
      </p>

      <Callout variant="tip" title="Good ideas">
        <ul>
          <li>Pajama day at $300 raised</li>
          <li>Read-outside Friday at 2,000 minutes</li>
          <li>Teacher reads a chapter book in a silly voice at $750</li>
        </ul>
      </Callout>

      <h2>Setting them up</h2>
      <p>
        From your classroom dashboard (or, for admins, the classroom page), click <strong>Goals &amp; rewards</strong>. Set a fundraising goal, a reading goal, both, or neither — we suggest a reading goal worked out from your class size and the campaign's length. To add a reward, click <strong>Add a classroom reward</strong>, name the prize, pick whether it's counted by money or minutes, and set the amount it unlocks at.
      </p>
      <p>
        For the amount, aim lower than school rewards — a class of 25 isn't going to raise $1,000 alone, but $300 is in reach. And use specific names — telling a student "if our class raises $300, we get pajama day Friday" is much more motivating than "extra recess."
      </p>
      <p>
        You can edit or remove these any time before the campaign wraps up.
      </p>
      <p>
        Have a kind of reward in mind that PagePledge doesn't offer? <a href={contactMailto("Feature Request")}>Email us a feature request</a>. The same link is under your list of rewards.
      </p>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/setting-school-milestones">School rewards</Link>.</li>
        <li><Link href="/help/articles/setting-student-prizes">Student prizes</Link>.</li>
        <li><Link href="/help/articles/teacher-classroom-dashboard">Your classroom dashboard</Link>.</li>
      </ul>
    </>
  );
}
